/**
 * Map document type from database type and file_url to actual document type
 * KISS: Simple function that extracts type from file_url when type is 'other'
 *
 * @param dbType - The type stored in database ('identity', 'license', 'other')
 * @param fileUrl - The file URL containing the actual type in the filename
 * @returns The mapped document type
 */
export function mapDocumentType(dbType: string, fileUrl: string): string {
  // If type is already specific, return it (but map identity to identity_card for consistency)
  if (dbType !== "other" && dbType !== "license") {
    // Map "identity" to "identity_card" for tenant documents consistency
    if (dbType === "identity") {
      // Check if it's a tenant document by looking at file_url
      // Tenant documents use identity_card, host documents use identity
      // For now, we'll check the file_url pattern or return identity_card as default for tenant context
      // This will be handled by the caller if needed
      return dbType;
    }
    return dbType;
  }

  // Extract type from file_url (format: .../userId/type_timestamp.ext)
  const urlParts = fileUrl.split("/");
  const fileName = urlParts[urlParts.length - 1] || "";

  // Log pour débogage
  if (import.meta.env.DEV) {
    console.log("[mapDocumentType] Mapping:", {
      dbType,
      fileUrl,
      fileName,
    });
  }

  // Remove timestamp and extension to get the type
  // Pattern: type_timestamp.ext (ex: vehicle_registration_1763406206584.pdf, driver_license_1763406206584.pdf)
  // Regex corrigé pour capturer les types avec underscores (driver_license, vehicle_registration, etc.)
  const typeMatch = fileName.match(/^([^_]+(?:_[^_]+)*)_\d+/);
  if (typeMatch) {
    const extractedType = typeMatch[1];

    if (import.meta.env.DEV) {
      console.log("[mapDocumentType] Extracted type:", extractedType);
    }

    // Map common types - includes all document types from the enum
    const typeMap: Record<string, string> = {
      identity: "identity",
      identity_card: "identity_card",
      passport: "passport",
      driver_license: "driver_license",
      license: "driver_license",
      vehicle_registration: "vehicle_registration",
      insurance: "insurance",
      technical_inspection: "technical_inspection",
      trade_register: "trade_register",
      business_premises_photo: "business_premises_photo",
      proof_of_address: "proof_of_address",
      bank_details: "bank_details",
    };

    const mappedType = typeMap[extractedType] || extractedType;

    if (import.meta.env.DEV) {
      console.log("[mapDocumentType] Mapped to:", mappedType);
    }

    return mappedType;
  }

  // Fallback: map database types
  if (dbType === "license") {
    return "driver_license";
  }

  if (import.meta.env.DEV) {
    console.log("[mapDocumentType] No match found, returning dbType:", dbType);
  }

  return dbType;
}

/**
 * Map document type from UI to database format
 * @param uiType - The type used in UI (identity, driver_license, vehicle_registration, etc.)
 * @returns The database type ('identity', 'license', 'other')
 */
// Valid document_type enum values in the database
const VALID_DOCUMENT_TYPES = [
  "identity",
  "license",
  "other",
  "vehicle_registration",
  "insurance",
  "technical_inspection",
  "identity_card",
  "business_premises_photo",
  "trade_register",
  "driver_license",
  "proof_of_address",
  "bank_details",
] as const;

type DocumentTypeDb = (typeof VALID_DOCUMENT_TYPES)[number];

/**
 * Map document type from UI to database format.
 * Now passes through the actual type since DB enum supports all types.
 */
export function mapDocumentTypeToDb(uiType: string): DocumentTypeDb {
  // Normalize common aliases
  const aliasMap: Record<string, DocumentTypeDb> = {
    passport: "identity", // passport maps to identity in DB
    identity_card: "identity_card", // keep as is - it's in the enum
  };

  // Use alias if exists, otherwise use the type directly
  const mappedType = aliasMap[uiType] || uiType;

  // Validate against known enum values
  if (VALID_DOCUMENT_TYPES.includes(mappedType as DocumentTypeDb)) {
    return mappedType as DocumentTypeDb;
  }

  // Fallback for unknown types
  console.warn(`[mapDocumentTypeToDb] Unknown type "${uiType}", falling back to "other"`);
  return "other";
}
